/**
 * Dex live-scan duplicate-frame suppressor — scan-speed (2026-06-22).
 *
 * WHY THIS EXISTS. The iOS Broadcast Upload Extension (SampleHandler.swift)
 * writes a JPEG at a fixed ~1 Hz cadence whether or not the on-screen Pokédex
 * has changed. When the trainer pauses on a row — reading it, deciding whether
 * to scroll, or simply holding the phone steady — the extension keeps emitting
 * the SAME frame every second. The App-Group poller dispatches each of those
 * byte-identical frames into `processFrame`, which then runs the full, costly
 * 5× Tesseract OCR fanout on pixels it has already read. The pre-#472 device
 * logs show exactly this: long runs of repeated `frame loop tick` ticks and
 * "repeated same bytes" before any useful observation, with `inFlight` climbing
 * to 2 as the redundant OCR work backs up behind the 1 Hz arrivals.
 *
 * Re-OCRing an identical frame can only ever produce the identical read, which
 * the session already dedupes — so the work is pure waste. This module drops
 * that waste BEFORE the OCR fanout, which is the single biggest per-frame cost
 * in the loop.
 *
 * WHAT IT IS NOT. This is NOT a write gate and it NEVER relaxes one. It only
 * decides whether to spend an OCR pass on a frame whose content the loop has
 * ALREADY processed (or is processing right now). Every frame it admits flows
 * through the unchanged blur / scroll / pre-OCR / region / candidate gates
 * exactly as before. The accuracy contract is simple and total:
 *
 *   - A frame whose content differs in ANY way from the last admitted frame
 *     (a scroll, a newly-caught sprite, a variant swipe, a different overlay,
 *     even a single re-encoded pixel) hashes differently and is ALWAYS
 *     admitted. Suppression is exact-match only.
 *   - Suppressing a byte-identical frame removes zero evidence: the pixels are
 *     the same, so the read would be the same, so no candidate, observation, or
 *     commit can be lost by skipping it.
 *
 * Pure + framework-agnostic (no DOM, no canvas) so it is unit-tested
 * deterministically and adds only an O(n) string hash on the hot path — far
 * cheaper than the decode-and-OCR pass it guards.
 */

/**
 * 32-bit FNV-1a hash of a string, returned as an unsigned hex string. Used to
 * fingerprint a frame's data URL cheaply without holding the whole base64
 * payload for comparison. Collisions on a 32-bit hash are astronomically
 * unlikely between two DIFFERENT same-length JPEG payloads within a single
 * scan, and a collision can only ever cause a (harmless) extra skip of a frame
 * whose pixels are, by construction, indistinguishable from the last one — it
 * can never admit a wrong read. Pure and total.
 */
export function hashFrameContent(dataUrl: string): string {
  let h = 0x811c9dc5;
  // Strided (2026-09-22, Galaxy S21+ scan speed): every 16th character plus
  // the tail, not all ~300k. A JPEG's entropy coding smears any pixel change
  // across the rest of the stream, so a sample this dense still separates
  // frames that differ at all; and a collision only ever costs one harmless
  // skip of a frame indistinguishable from the last (see above).
  const n = dataUrl.length;
  const stride = n > 4096 ? 16 : 1;
  for (let i = 0; i < n; i += stride) {
    h ^= dataUrl.charCodeAt(i);
    // 32-bit FNV prime multiply via shifts to stay in integer range.
    h = (h + ((h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24))) >>> 0;
  }
  for (let i = Math.max(0, n - 256); i < n; i++) {
    h ^= dataUrl.charCodeAt(i);
    // 32-bit FNV prime multiply via shifts to stay in integer range.
    h = (h + ((h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24))) >>> 0;
  }
  // Fold the length in too, so two strings that hash-collide on content but
  // differ in length (impossible for identical frames, defensive otherwise)
  // separate cleanly.
  return `${(h >>> 0).toString(16)}:${dataUrl.length.toString(16)}`;
}

/**
 * Maximum times a held-steady, byte-identical frame may be RE-ADMITTED for OCR
 * while every prior read of that exact content came back UNPRODUCTIVE (zero
 * usable dex numbers). See the cold-start rationale on `markDone` below.
 */
export const COLD_START_RETRY_BUDGET = 3;

/**
 * Round-8 — count-based forced re-ingest of `duplicate_recent` frames.
 *
 * On a near-static, purified scan the trainer holds the phone steady on one
 * Pokédex view: the 1 Hz extension re-emits the SAME JPEG over and over, so the
 * dwell collapses to a single accepted read followed by a long run of
 * `duplicate_recent` skips (the device scan that motivated this measured 71 such
 * skips, with the top byte-fingerprints recurring 10–15× each). That starves the
 * session of the REPEAT high-histSim frames that low-id species (001–003) need
 * to corroborate across frames — they were cell-classified 35–42× but never
 * cleared the multi-frame confirmation, because only the first of each dwell run
 * ever reached the classifier.
 *
 * Fix: every Nth consecutive `duplicate_recent` for the SAME held view is FORCED
 * back through to OCR (verdict `forced_reingest`, admit=true) instead of being
 * dropped, so the classifier keeps seeing the repeat. N is small (3) so the
 * static view is re-read a few times per dwell — enough to corroborate — without
 * re-OCRing every 1 Hz frame (the cost the dedup exists to avoid). The measured
 * repeat depth of 10–15 means a depth-3 cadence yields ~3–5 forced re-reads per
 * dwell. This is NOT a write gate: a forced re-ingest flows through every
 * unchanged blur / scroll / region / corroboration / demotion gate exactly as a
 * normal admit, and the R7 zero-histSim demotion re-fires on each pass, so a
 * phantom (#024) stays blocked no matter how many times it is re-ingested.
 *
 * Scope is `duplicate_recent` ONLY — `duplicate_in_flight` is never forced,
 * because OCR is genuinely busy and piling onto a 2-deep queue helps nothing.
 */
export const DUPLICATE_RECENT_REINGEST_INTERVAL = 3;

/**
 * Round-12 / 2026-06-25 — PURIFIED-ONLY relaxed re-ingest interval.
 *
 * The R8 interval of 3 drops 2 of every 3 `duplicate_recent` frames. For a
 * purified scan the trainer holds the phone on a static cell while the genuine
 * catch's species-histogram oscillates around the write floor: those repeat
 * on-screen views are exactly the corroborating sightings the session's purified
 * ratio gate needs, but the 1-in-3 cadence starves them. When the LAST completed
 * read of the held view was purified (`markDone(..., "purified")`), we re-ingest
 * every OTHER duplicate (drop, FORCE, drop, FORCE) so a stationary purified cell
 * reaches its 2-positive confirmation in a few held frames — without re-OCRing
 * every single 1 Hz arrival (the cost the dedup exists to avoid).
 *
 * Scope is strictly purified: any other (or unknown) last-done variant keeps the
 * R8 interval of 3, byte-for-byte. This is NOT a write relaxation — a forced
 * re-ingest still flows through every downstream gate (blur / scroll / region /
 * the R7 zero-histSim demotion / the session's 2-positive ratio), and because
 * each forced frame is a distinct 1 Hz ARRIVAL processed exactly once, it can
 * only ever contribute at most ONE positive: a single physical frame is never
 * double-counted into two positives.
 */
export const PURIFIED_DUPLICATE_RECENT_REINGEST_INTERVAL = 2;

/**
 * Re-ingest interval while the session says cells are WAITING (2026-07-23).
 *
 * The evidence engine commits an ambiguous cell on its second agreeing look. A
 * user who stops on a row expects it to fill in; instead the fixed interval of 3
 * meant a held view was re-read every ~1.5s at 2fps, and a row could sit on
 * screen a long time still one vote short — the "hold still and nothing happens,
 * scroll and it commits" complaint. When cells are actually pending, a tighter
 * cadence is the whole fix, and it costs nothing on a scan that has nothing
 * waiting because the demand flag is false there.
 */
export const PENDING_DUPLICATE_RECENT_REINGEST_INTERVAL = 2;

/**
 * Hard cap on forced re-reads of ONE held view, after which a static screen is
 * left completely alone until its content changes.
 *
 * This is the thermal bound. Everything above makes a stationary screen cost
 * MORE work; without a ceiling, a phone parked on the Pokédex (or left face-up
 * mid-scan) would re-OCR the same pixels indefinitely, which on a device already
 * running a ReplayKit capture is exactly how a scan turns into a hot phone and a
 * laggy PoGo.
 *
 * A cap is safe because re-reads have sharply diminishing returns: the same
 * pixels through the same classifier give the same verdict, so the value is in
 * the first few looks (enough to satisfy the evidence bar) and there is none
 * after that. 8 covers the 2-vote bar several times over even if some reads are
 * unproductive. The counter resets the instant the view changes, so a user who
 * is actually scanning never meets it — it only catches a screen nobody is
 * driving.
 */
export const MAX_FORCED_REINGESTS_PER_VIEW = 8;

/** Why a frame was admitted or suppressed by the dedup pass. */
export type FrameDedupVerdict =
  /** Content differs from the last admitted frame — run the full pipeline. */
  | "admit"
  /**
   * Byte-identical to the most recently completed frame whose read was
   * UNPRODUCTIVE — re-admitted (within the cold-start budget) so a warmed-up OCR
   * engine can re-read the same static frame the cold first pass failed on.
   */
  | "cold_retry"
  /**
   * Byte-identical to the most recently completed frame, but this is the Nth
   * consecutive such duplicate for the held view, so it is FORCED back through to
   * OCR (admit=true) instead of dropped — see
   * `DUPLICATE_RECENT_REINGEST_INTERVAL`. Lets a near-static scan keep feeding the
   * classifier the repeat frames low-id species need to corroborate.
   */
  | "forced_reingest"
  /** Byte-identical to the frame currently in flight — skip (no new work). */
  | "duplicate_in_flight"
  /** Byte-identical to the most recently completed frame — skip (no new read). */
  | "duplicate_recent";

export interface FrameDedupDecision {
  verdict: FrameDedupVerdict;
  /** True for `admit`, `cold_retry`, and `forced_reingest`. */
  admit: boolean;
  /** The content hash computed for this frame (echoed for telemetry/tests). */
  hash: string;
  /**
   * For `forced_reingest`: the consecutive-`duplicate_recent` count that tripped
   * the re-ingest (always === `DUPLICATE_RECENT_REINGEST_INTERVAL`). Echoed for
   * telemetry. Omitted for every other verdict.
   */
  dupCount?: number;
  /** For `forced_reingest`: the interval constant, echoed for telemetry. */
  interval?: number;
}

/**
 * Create a stateful duplicate-frame suppressor for one scan session.
 *
 * Lifecycle, mirroring how `processFrame` already brackets its OCR work:
 *
 *   const dedup = createFrameDedup();
 *   const d = dedup.consider(frame.dataUrl);
 *   if (!d.admit) { /* skip OCR, note telemetry *\/ return; }
 *   dedup.markInFlight(d.hash);       // before awaiting OCR
 *   try { ...OCR + ingest... }
 *   finally { dedup.markDone(d.hash); } // mirrors inFlightRef decrement
 *
 * The suppressor tracks two things:
 *
 *   1. The set of content hashes whose OCR is currently IN FLIGHT. Because the
 *      loop allows up to 2 concurrent OCR passes, two byte-identical frames can
 *      arrive within the same ~second; the second must not launch a redundant
 *      pass while the first is still running. This is the direct fix for the
 *      "inFlight hit 2 with repeated same bytes" symptom.
 *
 *   2. The hash of the most recently COMPLETED frame, so a held-steady screen
 *      that emits the same JPEG every second is read once and then skipped
 *      until its content actually changes.
 *
 * We deliberately remember only the LAST completed hash (not a history set): a
 * scan that returns to an earlier-seen screen SHOULD re-read it (the trainer
 * may have caught something in between), so dedup must be strictly local to the
 * current dwell. A genuine back-scroll changes the content hash on the way
 * there and back, so it is always admitted.
 */
export function createFrameDedup() {
  const inFlight = new Set<string>();
  let lastDoneHash: string | null = null;
  // Whether the LAST completed read of `lastDoneHash` yielded usable evidence
  // (≥1 dex number). A productive read of static content is suppressed normally
  // (a re-read of identical pixels yields the identical numbers); an
  // UNPRODUCTIVE one is re-admittable for a bounded number of cold-start retries.
  let lastDoneProductive = true;
  // Consecutive unproductive completions of `lastDoneHash`, capped by the budget.
  let coldRetries = 0;
  // Round-8: consecutive frames that would have been dropped as
  // `duplicate_recent` for the CURRENT held view. Every
  // DUPLICATE_RECENT_REINGEST_INTERVAL-th one is forced back through to OCR
  // (drop, drop, RE-INGEST, …) so a near-static scan keeps feeding the
  // classifier repeat frames. Reset to 0 the moment a genuinely new frame is
  // admitted (content change) or the session resets — i.e. when the held view
  // breaks. Scoped to `duplicate_recent`; `duplicate_in_flight` never touches it.
  let recentDuplicateStreak = 0;
  /** Forced re-reads spent on the CURRENT held view; see MAX_FORCED_REINGESTS_PER_VIEW. */
  let forcedReingestsThisView = 0;
  // Round-12: the variant of the LAST completed read of the current held view,
  // supplied by `markDone`. A held-steady dwell keeps the same on-screen variant
  // across its byte-identical frames, so the last-done variant is the held view's
  // variant — which lets `consider` pick the purified-relaxed re-ingest interval
  // for a purified dwell and the unchanged R8 interval for everything else. Null
  // until the first completed read, and reset between sessions.
  let lastDoneVariant: string | null = null;

  return {
    /**
     * Decide whether a frame's content warrants an OCR pass. Does NOT record the
     * frame as in-flight (call `markInFlight` for that once the caller commits to
     * processing it), so the in-flight accounting is untouched here. It DOES,
     * however, advance the Round-8 consecutive-`duplicate_recent` counter that
     * drives forced re-ingest, since the frames being counted are the dropped
     * ones that never reach `markInFlight`/`markDone`; call `consider` exactly
     * once per arriving frame.
     */
    consider(dataUrl: string, opts?: { demand?: boolean }): FrameDedupDecision {
      const demand = opts?.demand === true;
      const hash = hashFrameContent(dataUrl);
      if (inFlight.has(hash)) {
        return { verdict: "duplicate_in_flight", admit: false, hash };
      }
      if (lastDoneHash != null && hash === lastDoneHash) {
        // Held-steady identical content. Normally skip — re-OCRing the same
        // pixels can only repeat the same read. EXCEPTION (cold start): while
        // every prior read of THIS exact content came back unproductive (the
        // classic cold-OCR-engine / mid-load empty read that stalls the scanner
        // until the user scrolls), re-admit it a bounded number of times so a
        // warmed-up engine gets a chance to read the static frame it first
        // failed on. This never relaxes a WRITE gate: an admitted retry still
        // flows through the unchanged blur / scroll / region / two-frame
        // corroboration gates, and a productive read immediately ends the retries.
        if (!lastDoneProductive && coldRetries < COLD_START_RETRY_BUDGET) {
          return { verdict: "cold_retry", admit: true, hash };
        }
        // Round-8 count-based forced re-ingest. This frame would normally be
        // dropped as `duplicate_recent`; count it against the current held view
        // and, every DUPLICATE_RECENT_REINGEST_INTERVAL-th consecutive
        // duplicate, force it through to OCR instead so the classifier keeps
        // seeing the repeat frames low-id species need to corroborate. The
        // counter resets the instant the view changes (an `admit` below) or the
        // session resets. NOT a write relaxation: a forced re-ingest runs every
        // downstream gate — including the R7 zero-histSim demotion — exactly as a
        // normal admit, so a phantom never gains a write by being re-read.
        recentDuplicateStreak += 1;
        // THERMAL BOUND (2026-07-23). Past this many forced re-reads of the same
        // held view, stop forcing entirely: the pixels have not changed, the
        // classifier has already given its answer several times over, and further
        // passes only heat the device. Resets when the view changes.
        if (forcedReingestsThisView >= MAX_FORCED_REINGESTS_PER_VIEW) {
          return { verdict: "duplicate_recent", admit: false, hash };
        }
        // Round-12: a purified held view re-ingests on the relaxed interval so its
        // repeat on-screen views reach the session's 2-positive gate; every other
        // (or unknown) variant keeps the R8 interval byte-for-byte.
        //
        // 2026-07-23: a caller that reports PENDING cells (partial evidence
        // waiting on one more look) gets the tighter interval too — that is the
        // only state in which re-reading a static screen can change an outcome.
        const interval =
          lastDoneVariant === "purified"
            ? PURIFIED_DUPLICATE_RECENT_REINGEST_INTERVAL
            : demand
              ? PENDING_DUPLICATE_RECENT_REINGEST_INTERVAL
              : DUPLICATE_RECENT_REINGEST_INTERVAL;
        if (recentDuplicateStreak >= interval) {
          recentDuplicateStreak = 0;
          forcedReingestsThisView += 1;
          return {
            verdict: "forced_reingest",
            admit: true,
            hash,
            dupCount: interval,
            interval,
          };
        }
        return { verdict: "duplicate_recent", admit: false, hash };
      }
      // Genuinely new content (or the first frame of the session): the held view
      // has broken, so the consecutive-duplicate streak no longer applies — and
      // neither does the forced-re-read budget, which is per HELD VIEW.
      recentDuplicateStreak = 0;
      forcedReingestsThisView = 0;
      return { verdict: "admit", admit: true, hash };
    },

    /** Record that an admitted frame's OCR has started. */
    markInFlight(hash: string): void {
      inFlight.add(hash);
    },

    /**
     * Record that a frame's processing finished (success or failure). Clears
     * the in-flight marker and adopts the hash as the most-recent-completed
     * fingerprint so the next identical dwell frame is suppressed. Mirrors the
     * `inFlightRef` decrement in `processFrame`'s `finally`.
     *
     * `productive` is true when the frame yielded usable evidence (≥1 dex
     * number). An unproductive completion of the SAME content advances the
     * cold-start retry counter; a productive read (or a change of content)
     * resets it, so the bounded re-admission only ever spans a cold-start stall.
     */
    markDone(hash: string, productive = true, variant: string | null = null): void {
      inFlight.delete(hash);
      if (hash === lastDoneHash && !productive) {
        coldRetries += 1;
      } else {
        coldRetries = 0;
      }
      lastDoneHash = hash;
      lastDoneProductive = productive;
      // Round-12: remember the variant of the held view so the next duplicate of
      // this exact content picks the right (purified-relaxed vs R8) re-ingest
      // interval. Only meaningful for `duplicate_recent` of the SAME content.
      lastDoneVariant = variant;
    },

    /** Reset between scan sessions so a prior dwell can't suppress a new scan. */
    reset(): void {
      inFlight.clear();
      lastDoneHash = null;
      lastDoneProductive = true;
      coldRetries = 0;
      recentDuplicateStreak = 0;
      // A new session must start with a full forced-re-read budget. `consider`
      // also zeroes this on the first `admit`, so the omission was usually
      // masked — but a reset() with no reset of the per-view budget left a
      // stale exhausted count that suppressed re-reads for any dwell that
      // opened before the first content change.
      forcedReingestsThisView = 0;
      lastDoneVariant = null;
    },

    /** Test/diagnostic peek at the in-flight count. */
    inFlightCount(): number {
      return inFlight.size;
    },
  };
}

export type FrameDedup = ReturnType<typeof createFrameDedup>;
